// IMP-03 — one reusable "are you sure?" dialog for destructive / irreversible
// actions (deletes, cancels, force-logout). Promise-based so callers read
// linearly:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title, description, confirmLabel }))) return;
//   ... do the destructive thing ...
//
// Mounted once at the app root via <ConfirmProvider>. Replaces the ad-hoc
// browser window.confirm() prompts with the branded AlertDialog.
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

const ConfirmCtx = createContext(() => Promise.resolve(false));
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => (
    new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        description: opts.description || "This action can't be undone.",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        destructive: opts.destructive !== false, // default to destructive styling
      });
    })
  ), []);

  const settle = useCallback((val) => {
    setState(null);
    const r = resolver.current;
    resolver.current = null;
    r?.(val);
  }, []);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) settle(false); }}>
        {state && (
          <AlertDialogContent data-testid="confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="confirm-dialog-title">{state.title}</AlertDialogTitle>
              <AlertDialogDescription data-testid="confirm-dialog-description" className="whitespace-pre-line">
                {state.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={() => settle(false)}>
                {state.cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-dialog-confirm"
                onClick={() => settle(true)}
                className={state.destructive ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-600" : ""}
              >
                {state.confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export default ConfirmProvider;
